import { logoutRequest } from '@/features/auth/api';
import { ApiError } from '@/shared/lib/api-client';
import { useAuthStore } from '@/shared/stores/auth-store';
import { LogOut, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

/**
 * Banner amarelo fixo no topo do app quando a sessão é de support login
 * (super-admin entrou como admin do tenant via /auth/support/select-tenant).
 *
 * Mostra o email do support_user (impersonator) + qual tenant ele está dentro
 * + botão "Sair do suporte" que faz logout normal.
 *
 * Renderiza null quando user não é support — não atrapalha a UI.
 */
export function SupportBanner() {
  const user = useAuthStore((s) => s.user);
  const clearSession = useAuthStore((s) => s.clearSession);
  const [exiting, setExiting] = useState(false);

  if (!user?.impersonating) return null;

  const handleExit = async (): Promise<void> => {
    setExiting(true);
    try {
      await logoutRequest();
    } catch (err) {
      // logout failure não bloqueia clearSession local
      if (err instanceof ApiError) {
        toast.error('Falha ao sair limpo', { description: err.problem.detail ?? err.problem.title });
      }
    } finally {
      clearSession();
      window.location.href = '/login';
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 border-b border-warning-700/30 bg-warning-500 px-4 py-2 text-warning-900">
      <div className="flex items-center gap-2 text-[12px] font-semibold">
        <ShieldCheck size={14} />
        <span>
          Sessão de suporte ativa — Logado como{' '}
          <span className="font-bold">{user.name}</span> ({user.email}) via{' '}
          <span className="font-mono">{user.impersonating.email}</span>
        </span>
      </div>
      <button
        type="button"
        onClick={() => void handleExit()}
        disabled={exiting}
        className="inline-flex items-center gap-1.5 rounded-md bg-warning-700 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white hover:bg-warning-700/90 disabled:opacity-60"
      >
        <LogOut size={11} />
        Sair do suporte
      </button>
    </div>
  );
}
